import ContextEdgeMenu, {
  ContextEdgeMenuProps,
} from "@features/builder/graph/contextMenu/ContextEdgeMenu"
import ContextNodeMenu, {
  ContextNodeMenuProps,
} from "@features/builder/graph/contextMenu/ContextNodeMenu"
import ContextPaneMenu, {
  ContextPaneMenuProps,
} from "@features/builder/graph/contextMenu/ContextPaneMenu"
import "@features/builder/graph/graph-editor.css"
import {
  Background,
  BackgroundVariant,
  Connection,
  ConnectionLineType,
  Controls,
  Edge,
  EdgeTypes,
  FinalConnectionState,
  Node,
  NodeChange,
  OnBeforeDelete,
  OnConnect,
  OnInit,
  OnMove,
  OnNodeDrag,
  ReactFlow,
  SelectionMode,
  applyNodeChanges,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useStoreApi,
} from "@xyflow/react"
import "@xyflow/react/dist/base.css"
import "@xyflow/react/dist/style.css"
import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import css from "./Graph.module.css"
import CursorOverlay from "@features/builder/EditorPoc/CursorOverlay"
import { changeGroup } from "@features/builder/graph/changeGroup"
import { resizeGroupParents } from "@features/builder/graph/changeGroup/resizeGroupParents"
import { FitViewButton } from "@features/builder/graph/controls/FitViewButton"
import { GraphInteractionToggleButton } from "@features/builder/graph/controls/LockGraphToggleButton"
import { SelectInsideGroupToggleButton } from "@features/builder/graph/controls/SelectInsideGroupToggleButton"
import { ZoomInButton } from "@features/builder/graph/controls/ZoomInButton"
import { ZoomOutButton } from "@features/builder/graph/controls/ZoomOutButton"
import ReactFlowDevTools from "@features/builder/graph/devtools"
import {
  collapseGroup,
  createEmptyNode,
  expandGroup,
  findMaxId,
  highlightSelectedNodes,
} from "@features/builder/graph/helpers"
import {
  detectIntersections,
  getNodesIntersectingWithGroup,
} from "@features/builder/graph/intersection"
import MovableEdge from "@features/builder/graph/edge/MovableEdge"
import {
  NodeLevelOfDetails,
  nodeTypesLod1,
  nodeTypesLod2,
  nodeTypesLod3,
} from "@features/builder/graph/nodeTypes"
import { AppNode, EdgeData, NodeType } from "@features/builder/graph/types"
import { addEdgeToNode } from "@features/builder/helpers/add-edge"
import {
  areLagsValid,
  getEdgeLock,
  hasEdgeCycle,
  isSourceMissingUbound,
} from "@features/builder/helpers/edges"
import { getChildrenNodes, getNodeLock, getNodeZIndex } from "@features/builder/helpers/nodes"
import { removeEdge, removeEdgeFromNode } from "@features/builder/helpers/remove-edge"
import {
  socketJoinCrdtRoom,
  socketJoinCursorRoom,
  socketLeaveCrdtRoom,
  socketLeaveCursorRoom,
  useSocketIO,
} from "@features/builder/hooks/socketIOHooks"
import { useLastNodesChanges } from "@features/builder/hooks/useLastNodesChanges"
import NodeDrawer from "@features/builder/NodeDrawer/NodeDrawer"
import {
  CLOSE_NODE_DRAWER_ON_BLUR_LS_KEY,
  CLOSE_NODE_DRAWER_ON_MULTISELECT_LS_KEY,
} from "@features/builder/NodeDrawer/NodeDrawerOptionsMenu"
import { KeyBinding } from "@features/keymap/KeyBinding"
import { useThrottle } from "@hooks/useThrottle"
import { Box, CircularProgress, Grid } from "@mui/material"
import type { Viewport, XYPosition } from "@xyflow/system"
import cls from "classnames/dedupe"
import { useAtom, useSetAtom } from "jotai"
import {
  AddNodeMode,
  addNodeModeAtom,
  graphAtom,
  isClosingNodeDrawerInvokedAtom,
  nextDrawerNodeIdAtom,
  useGraph,
  useSetNodesHighlighted,
} from "@features/builder/graph/graphAtoms"
import { useLocalStorage } from "usehooks-ts"
import { isTouchEvent } from "utils/dom"
import { RenameDialog } from "@features/builder/graph/contextMenu/RenameDialog"
import {
  nodesToDuplicateAtom,
  useCopyPaste,
} from "@features/builder/graph/contextMenu/useCopyPaste"
import { ActiveShortcutsInfoButton } from "@features/builder/graph/controls/ActiveShortcutsInfoButton"
import ConfirmDeletingNodesDialog from "@features/builder/graph/ConfirmDeletingNodesDialog"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import { useNodesValidationWorker } from "@features/builder/graph/nodes/validation/useNodesValidationWorker"
import { enqueueSnackbar } from "notistack"
import AddNodeButton from "@features/builder/AddNodeButton"
import GraphLockedInfo from "@features/builder/graph/GraphLockedInfo"
import { shortcuts } from "@features/keymap/shortcuts"
import { useGetLinkedNode } from "@features/builder/hooks/useGetLinkedNode"
import logger from "@utils/logger"
import { useGraphDocumentData } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocumentData"
import { createEdgesDialogAtom } from "@features/builder/CreateEdges/createEdgesDialogAtom"
import { SetState } from "@custom-types/utils"
import useLockedNodesIds from "@features/builder/hooks/useLockedNodesIds"
import { ErrorsDrawer } from "@features/builder/ErrorsDrawer/ErrorsDrawer"

export type GraphProps = {
  nodes: AppNode[]
  edges: Edge<EdgeData>[]
  graphId?: string
}

const MOUSE_THROTTLE_INTERVAL = 300

export const NODE_Z_INDEX = 1001
export const COLLAPSED_GROUP_Z_INDEX = NODE_Z_INDEX
export const EXPANDED_GROUP_Z_INDEX = 990
export const EDGE_Z_INDEX = 1000
export const DRAGGED_NODE_Z_INDEX = NODE_Z_INDEX + 1
export const DRAGGED_COLLAPSED_GROUP_Z_INDEX = COLLAPSED_GROUP_Z_INDEX + 1
export const DRAGGED_EXPANDED_GROUP_Z_INDEX = EXPANDED_GROUP_Z_INDEX + 1

export const snapToGrid = {
  size: 20,
  active: import.meta.env.VITE_BUILD_SNAP_TO_GRID === "true",
  round(n: number) {
    return this.active ? Math.round(n / this.size) * this.size : n
  },
  ceil(n: number) {
    return this.active ? Math.ceil(n / this.size) * this.size : n
  },
  floor(n: number) {
    return this.active ? Math.floor(n / this.size) * this.size : n
  },
} as const

const edgeTypes: EdgeTypes = {
  floating: MovableEdge,
}

export const Graph = (props: GraphProps) => {
  const [nodes, setNodes] = useNodesState<AppNode>([])
  const [edges, setEdges, onEdgesChanges] = useEdgesState<Edge>([])
  const [nodeMenu, setNodeMenu] = useState<ContextNodeMenuProps | null>(null)
  const [paneMenu, setPaneMenu] = useState<ContextPaneMenuProps | null>(null)
  const [edgeMenu, setEdgeMenu] = useState<ContextEdgeMenuProps | null>(null)
  const [canSelectInsideGroup, setCanSelectInsideGroup] = useState(false)
  const { isLocked, remotelyLockedBy } = useGraph()
  const setGraph = useSetAtom(graphAtom)
  const setIsLocked = useCallback(
    (valueOrUpdater: boolean | ((prev: boolean) => boolean)) => {
      setGraph((prev) => ({
        ...prev,
        isLocked:
          typeof valueOrUpdater === "function" ? valueOrUpdater(prev.isLocked) : valueOrUpdater,
      }))
    },
    [setGraph],
  )
  const [isRendering, setIsRendering] = useState(true)
  const [nodesToConfirmDeleting, setNodesToConfirmDeleting] = useState<AppNode[]>([])
  const [nodesLod, setNodesLod] = useState(NodeLevelOfDetails.WithoutText)
  const [addNodeMode, setAddNodeModeAtom] = useAtom(addNodeModeAtom)
  const { setLastNodesChanges } = useLastNodesChanges(nodes)
  const [closeOnBlur] = useLocalStorage(CLOSE_NODE_DRAWER_ON_BLUR_LS_KEY, true)
  const [closeOnMultiselect] = useLocalStorage(CLOSE_NODE_DRAWER_ON_MULTISELECT_LS_KEY, true)
  const [nodesToDuplicate] = useAtom(nodesToDuplicateAtom)
  const { handleCopyNodes, handlePasteNodes } = useCopyPaste()
  const { clear: clearNodesValidation, removeNodeValidation } = useNodesValidation()
  const setNodesHighlighted = useSetNodesHighlighted()
  const setNextDrawerNodeId = useSetAtom(nextDrawerNodeIdAtom)
  const setIsClosingNodeDrawerInvoked = useSetAtom(isClosingNodeDrawerInvokedAtom)
  const setCreateEdgesDialogOpen = useSetAtom(createEdgesDialogAtom)
  const graphDocumentData = useGraphDocumentData()
  useNodesValidationWorker({
    nodes,
    graphDocumentData,
    cacheConfig: {
      ttlMinutes: 60 * 24 * 7,
    },
  })

  const cursorPosition = useRef<XYPosition>()
  const isDraggingNodesRef = useRef(false)
  /**
   * Flag to coordinate node deletion flows and prevent duplicate parent_lst updates.
   *
   * PROBLEM:
   * When nodes are deleted, React Flow triggers both edge removal AND node removal events.
   * This causes two conflicting code paths to update parents_lst:
   * 1. handleEdgesDelete → removeEdge() (N individual updates for each edge)
   * 2. handleNodesDelete → batch update (1 optimized update for all affected nodes)
   *
   * Without coordination, this results in:
   * - Duplicate/conflicting updates to the same data
   * - Performance degradation (N+1 updates instead of 1)
   * - Potential race conditions and inconsistent state
   *
   * SOLUTION:
   * This ref acts as a coordination flag between the two handlers:
   * - Set to TRUE in onBeforeDelete (before React Flow processes the deletion)
   * - handleEdgesDelete checks this flag and skips cleanup when TRUE
   * - handleNodesDelete performs the batch update and resets the flag
   * - Result: Single source of truth, better performance, no conflicts
   *
   * NOTE: This is a workaround for React Flow's event firing sequence.
   * We use a ref instead of state because we need synchronous updates that don't
   * trigger re-renders between the deletion handlers.
   */
  const isDeletingNodesRef = useRef(false)

  const socket = useSocketIO()

  const store = useStoreApi<AppNode>()
  const reactFlowInstance = useReactFlow<AppNode, Edge<EdgeData>>()
  const {
    addNodes,
    getEdges,
    getInternalNode,
    getIntersectingNodes,
    getNode,
    getNodes,
    screenToFlowPosition,
    updateNode,
  } = reactFlowInstance

  const ref = useRef<HTMLDivElement | null>(null)
  const hasAnyMenuOpen = Boolean(nodeMenu || paneMenu || edgeMenu)
  const ghostNodeRef = useRef<HTMLDivElement>()

  const { getLinkedNode } = useGetLinkedNode()
  const lockedNodesIds = useLockedNodesIds()

  useEffect(() => {
    setEdges(props.edges)
  }, [props.edges, setEdges])

  useEffect(() => {
    setNodes((nodes) => {
      // we want to retain selected state as this is not something that is being shared through crdt
      // so if we had node selected and we gonna receive update, we will be missing our selection - add it manually here.)
      const selectedIds = new Set(nodes.filter((n) => n.selected).map((n) => n.id))
      const updatedNodes = props.nodes.map((n) => ({
        ...n,
        selected: selectedIds.has(n.id),
      }))

      return updatedNodes
    })
  }, [props.nodes, setNodes])

  useEffect(() => {
    if (isLocked) {
      setPaneMenu(null)
      setNodeMenu(null)
      setEdgeMenu(null)
    }
  }, [isLocked])

  useEffect(() => {
    setGraph({
      graphId: props.graphId ?? null,
      isLocked: true,
      isRemotelyLockedBySomeoneElse: false,
      remotelyLockedBy: null,
    })
    socketJoinCrdtRoom(props.graphId)
    return () => {
      socketLeaveCrdtRoom(props.graphId)
      socketLeaveCursorRoom(props.graphId)
      setGraph({
        graphId: null,
        isLocked: true,
        isRemotelyLockedBySomeoneElse: false,
        remotelyLockedBy: null,
      })
      setAddNodeModeAtom(AddNodeMode.None)
      setNextDrawerNodeId(null)
      setIsClosingNodeDrawerInvoked(false)
      setNodesHighlighted({ groupId: "", nodesId: [] })
    }
  }, [
    props.graphId,
    setGraph,
    setAddNodeModeAtom,
    setNextDrawerNodeId,
    setIsClosingNodeDrawerInvoked,
    setNodesHighlighted,
  ])

  const applyIntersectionStyles = useCallback(
    (nodesForUpdate: AppNode[]) => {
      const { intersectingNode, intersectingGroup, childNodes } = detectIntersections(
        nodesForUpdate,
        getIntersectingNodes,
      )

      return nodesForUpdate.map((node) => ({
        ...node,
        className: cls(node.className, {
          [`${css.overlapWarning}`]:
            intersectingGroup.has(node.id) || intersectingNode.has(node.id),
          [`${css.overlapOpacity}`]: childNodes.has(node.id),
        }),
        // Patch snap to grid that's off by few pixels
        position: {
          x: snapToGrid.round(Number(node.position.x)),
          y: snapToGrid.round(Number(node.position.y)),
        },
      }))
    },
    [getIntersectingNodes],
  )

  const isValidConnection = useCallback(
    (connection: Edge | Connection) => {
      if (isLocked) return false

      const nodes = getNodes()
      const target = nodes.find((node) => node.id === connection.target)
      const source = nodes.find((node) => node.id === connection.source)

      if (!source || !target) return false
      if (source.type === NodeType.Group || target.type === NodeType.Group) return false
      if (target.id === connection.source) return false

      if (
        getNodeLock(props.graphId ?? "", source.id) ||
        getNodeLock(props.graphId ?? "", target.id)
      ) {
        enqueueSnackbar(
          "This connection cannot be created as the source or target node is locked",
          { variant: "error", preventDuplicate: true },
        )
        return false
      }

      if (!areLagsValid({ source, target })) {
        enqueueSnackbar(
          "This connection can not be created as child lag is greater than parent lag",
          {
            variant: "error",
            preventDuplicate: true,
          },
        )
        return false
      }

      const hasCycle = hasEdgeCycle({ edges: getEdges(), nodes, source, target })

      if (hasCycle) {
        enqueueSnackbar("This connection can not be created as it would create a cycle", {
          variant: "error",
          preventDuplicate: true,
        })
        return false
      }

      if (isSourceMissingUbound({ source, target })) {
        enqueueSnackbar("This connection can not be created as the parent node has no ubound set", {
          variant: "error",
          preventDuplicate: true,
        })
        return false
      }

      const haveLink = "metaNode" in target.data && !!target.data.metaNode.Link
      if (haveLink) {
        enqueueSnackbar(
          "This connection can not be created as Node with Link cannot have parent node",
          {
            variant: "error",
            preventDuplicate: true,
          },
        )
        return false
      }

      return true
    },
    [isLocked, getNodes, getEdges, props.graphId],
  )

  const handleConnect: OnConnect = useCallback((_connection) => {}, [])

  const handleNodeContextMenuOpen = useCallback(
    (event: ReactMouseEvent, node: Node) => {
      event.preventDefault()
      if (isLocked) return

      setPaneMenu(null)
      setEdgeMenu(null)
      setNodeMenu({
        id: node.id,
        x: event.clientX,
        y: event.clientY,
        onCloseClick: () => setNodeMenu(null),
      })
    },
    [isLocked, setNodeMenu],
  )

  const handlePaneClick = useCallback(
    (event: ReactMouseEvent) => {
      if (isLocked) return

      // remove selection from all nodes on clicking empty graph area
      nodes.forEach((n) => {
        if (n.selected) updateNode(n.id, { selected: false })
      })

      if (addNodeMode === AddNodeMode.CursorNode || addNodeMode === AddNodeMode.CursorGroup) {
        const position = screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        })
        if (addNodeMode === AddNodeMode.CursorNode) {
          const maxId = findMaxId(getNodes(), false)
          addNodes(createEmptyNode(NodeType.Empty, maxId, position, true))
          setNextDrawerNodeId(maxId)
          store.getState().resetSelectedElements()
          store.getState().addSelectedNodes([maxId])
        }
        if (addNodeMode === AddNodeMode.CursorGroup) {
          const maxId = findMaxId(getNodes(), true)
          addNodes(createEmptyNode(NodeType.Group, maxId, position, false))
          setNextDrawerNodeId(maxId)
        }
        setAddNodeModeAtom(AddNodeMode.None)
      } else {
        closeOnBlur && setIsClosingNodeDrawerInvoked(true)
      }

      setPaneMenu(null)
      setNodeMenu(null)
      setEdgeMenu(null)
    },
    [
      isLocked,
      addNodeMode,
      screenToFlowPosition,
      setNextDrawerNodeId,
      setIsClosingNodeDrawerInvoked,
      getNodes,
      addNodes,
      closeOnBlur,
      nodes,
      updateNode,
      store,
      setAddNodeModeAtom,
    ],
  )

  const handlePaneContextMenuOpen = useCallback(
    (event: MouseEvent | ReactMouseEvent) => {
      event.preventDefault()
      if (isLocked) return

      setNodeMenu(null)
      setEdgeMenu(null)
      setPaneMenu({
        x: event.clientX,
        y: event.clientY,
        onCloseClick: () => setPaneMenu(null),
      })
    },
    [isLocked],
  )

  const handleEdgeContextMenuOpen = useCallback(
    (event: MouseEvent | React.MouseEvent<Element, MouseEvent>, edge: Edge) => {
      event.preventDefault()
      if (isLocked) return

      setPaneMenu(null)
      setNodeMenu(null)
      setEdgeMenu({
        id: edge.id,
        y: event.clientY,
        x: event.clientX,
        onCloseClick: () => setEdgeMenu(null),
      })
    },
    [isLocked, setEdgeMenu],
  )

  const handleConnectEnd = useCallback(
    async (_event: MouseEvent | TouchEvent, connectionState: FinalConnectionState) => {
      if (
        !connectionState.isValid ||
        !connectionState?.toNode?.id ||
        !connectionState?.fromNode?.id
      ) {
        return
      }

      const toNode = getNode(connectionState.toNode.id)
      const fromNode = getNode(connectionState.fromNode.id)
      if (!toNode || !fromNode) return

      const linkedNodeId = "metaNode" in fromNode.data ? fromNode.data.metaNode?.Link : undefined
      const linkedNode = await getLinkedNode(linkedNodeId)
      if (linkedNodeId && !linkedNode) {
        enqueueSnackbar("Could not create connection: linked node not found", { variant: "error" })
        logger.error(`Linked node with id ${linkedNodeId} not found for node ${fromNode.id}`)
        return
      }

      const updatedNodes = addEdgeToNode(fromNode, toNode, getNodes(), linkedNode)

      updatedNodes.forEach((updatedNode) => {
        updateNode(updatedNode.id, updatedNode, { replace: true })
      })
    },

    [updateNode, getNode, getNodes, getLinkedNode],
  )

  const handleNodeDoubleClick = useCallback(
    (_event: ReactMouseEvent, node: AppNode) => {
      if (isLocked) return

      if (node.type === NodeType.Group) {
        const isCollapsed = node.data.metaGroup?.isCollapsed

        const res = isCollapsed ? expandGroup(node, getNodes()) : collapseGroup(node, getNodes())

        setNodes(res)
      }
    },
    [isLocked, getNodes, setNodes],
  )

  const handleMouseMove = useThrottle(
    useCallback(
      (e: ReactMouseEvent) => {
        cursorPosition.current = { x: e.clientX, y: e.clientY }

        if (addNodeMode !== AddNodeMode.None) {
          if (ghostNodeRef.current) {
            ghostNodeRef.current.style.left = `${e.clientX + 10}px`
            ghostNodeRef.current.style.top = `${e.clientY + 10}px`
          }
        }

        if (!props.graphId || !ref.current || hasAnyMenuOpen) {
          return
        }

        const cursorFlowPosition = screenToFlowPosition({ x: e.clientX, y: e.clientY })

        socket.emit("req_cursor", {
          id: props.graphId,
          x: cursorFlowPosition.x,
          y: cursorFlowPosition.y,
          rect: ref.current.getBoundingClientRect(),
        })
      },
      [addNodeMode, hasAnyMenuOpen, props.graphId, screenToFlowPosition, socket],
    ),
    MOUSE_THROTTLE_INTERVAL,
  )

  const handlePanMove: OnMove = useThrottle(
    useCallback(
      (e) => {
        if (!props.graphId || !ref.current || hasAnyMenuOpen || !e) {
          return
        }

        const x = isTouchEvent(e) ? e.touches[0]?.clientX ?? NaN : e.clientX
        const y = isTouchEvent(e) ? e.touches[0]?.clientY ?? NaN : e.clientY

        const cursorFlowPosition = screenToFlowPosition({ x, y })

        socket.emit("req_cursor", {
          id: props.graphId,
          x: cursorFlowPosition.x,
          y: cursorFlowPosition.y,
          rect: ref.current.getBoundingClientRect(),
        })
      },
      [hasAnyMenuOpen, props.graphId, screenToFlowPosition, socket],
    ),
    MOUSE_THROTTLE_INTERVAL,
  )

  const handleMouseEnter = useCallback(
    (e: ReactMouseEvent) => {
      if (!props.graphId || !ref.current) {
        return
      }

      const cursorFlowPosition = screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      })

      socketJoinCursorRoom({
        id: props.graphId,
        x: cursorFlowPosition.x,
        y: cursorFlowPosition.y,
        rect: ref.current.getBoundingClientRect(),
      })
    },
    [props.graphId, screenToFlowPosition],
  )

  const handleEdgesDelete = useCallback(
    (deletedEdges: Edge[]) => {
      // Skip edge cleanup if we're currently deleting nodes
      // (handleNodesDelete will handle all cleanup in one batch)
      if (isDeletingNodesRef.current) return

      for (const edge of deletedEdges) {
        removeEdge({ edgeId: edge.id, reactFlowInstance })
      }
    },
    [reactFlowInstance],
  )

  const handleNodesChange = useCallback(
    (nodeChanges: NodeChange<AppNode>[]) => {
      if (!props.graphId) return

      setLastNodesChanges(nodeChanges)

      setNodes((prevNodes) => {
        let updatedNodes = applyNodeChanges(nodeChanges, prevNodes)

        // highlight selected nodes and its edges, possibly close drawer on multiselect
        const selectionChanges = nodeChanges.filter((change) => change.type === "select")
        if (selectionChanges.length > 0) {
          const selectedNodes = updatedNodes.filter(({ selected }) => selected)
          const highlightResult = highlightSelectedNodes(selectedNodes, getEdges(), updatedNodes)
          updatedNodes = highlightResult.nodes
          setEdges(highlightResult.edges)

          if (closeOnMultiselect && selectedNodes.length > 1 && addNodeMode === AddNodeMode.None) {
            setIsClosingNodeDrawerInvoked(true)
          }
        }

        // process intersections etc. when only position or group size changes
        const includesPositionOrDimensionChange = nodeChanges.some((change) =>
          ["position", "dimensions"].includes(change.type),
        )
        if (includesPositionOrDimensionChange) {
          updatedNodes = applyIntersectionStyles(updatedNodes)
        }

        return updatedNodes
      })
    },
    [
      props.graphId,
      setLastNodesChanges,
      setNodes,
      getEdges,
      setEdges,
      closeOnMultiselect,
      addNodeMode,
      setIsClosingNodeDrawerInvoked,
      applyIntersectionStyles,
    ],
  )

  const handleViewportChange = useCallback((viewport: Viewport) => {
    ref.current?.style.setProperty("--flow-zoom", viewport.zoom.toString())

    if (viewport.zoom < 0.2) {
      setNodesLod(NodeLevelOfDetails.WithoutText)
    } else if (viewport.zoom < 0.6) {
      setNodesLod(NodeLevelOfDetails.WithoutType)
    } else {
      setNodesLod(NodeLevelOfDetails.Detailed)
    }
  }, [])

  const nodeTypes = useMemo(() => {
    switch (nodesLod) {
      case NodeLevelOfDetails.Detailed:
        return nodeTypesLod1
      case NodeLevelOfDetails.WithoutType:
        return nodeTypesLod2
      case NodeLevelOfDetails.WithoutText:
        return nodeTypesLod3
    }
  }, [nodesLod])

  const handleNodeClick = useCallback(
    (event: ReactMouseEvent, node: AppNode) => {
      // Allow click on group icon when interactiveStatus is false
      if (
        isLocked &&
        node.type === NodeType.Group &&
        "nodeName" in event.target &&
        event.target.nodeName === "path"
      ) {
        const isCollapsed = node.data.metaGroup?.isCollapsed
        const res = isCollapsed ? expandGroup(node, getNodes()) : collapseGroup(node, getNodes())

        setNodes(res)
      } else if (isLocked) {
        const clonedNodes = structuredClone(getNodes())
        // remove all selection
        clonedNodes.forEach((n) => {
          if (n.selected) {
            updateNode(n.id, {
              selected: n.id === node.id,
            })
          }
        })
      }
    },
    [isLocked, getNodes, setNodes, updateNode],
  )

  const handleInit: OnInit<AppNode, Edge> = useCallback(
    (instance) => {
      setTimeout(() => {
        setIsRendering(false)
        instance.fitView()
        clearNodesValidation()
      }, 0)
    },
    [clearNodesValidation],
  )

  const handleNodeDragStart = useCallback(
    (_event: ReactMouseEvent, _node: AppNode, nodes: AppNode[]) => {
      if (isLocked) return
      isDraggingNodesRef.current = true

      const updatedNodes = structuredClone(nodes)

      for (const node of updatedNodes) {
        updateNode(node.id, {
          ...node,
          zIndex: getNodeZIndex(node, { isDragging: true }),
          extent: undefined,
        })
      }
    },
    [isLocked, updateNode],
  )

  const handleNodeDragStop = useCallback(
    (event: ReactMouseEvent, _node: AppNode, nodes: AppNode[]) => {
      if (isLocked) return
      isDraggingNodesRef.current = false

      const resetedNodes = nodes.map((n) => ({
        ...n,
        // Reset zIndex after drag stop
        zIndex: getNodeZIndex(n),
      }))

      const flowPosition = reactFlowInstance.screenToFlowPosition({
        y: event.clientY,
        x: event.clientX,
      })
      const intersecting = getNodesIntersectingWithGroup(
        resetedNodes,
        flowPosition,
        reactFlowInstance,
      )

      // Move nodes/groups from/to group
      let updatedAllNodes = changeGroup({
        selectedNodes: resetedNodes,
        allNodes: getNodes(),
        getInternalNode,
        intersecting,
      })

      updatedAllNodes = resizeGroupParents({
        selectedNodes: resetedNodes,
        allNodes: updatedAllNodes,
      })

      // Sometimes `dragging` property is not removed from the node what can cause some implications
      // f.e. node can seamlessly select itself like in this ticket: https://dev.azure.com/spa-ihsmarkit/seawise/_workitems/edit/529385
      updatedAllNodes = updatedAllNodes.map((node) => {
        if (!node.dragging) return node
        const updateNode = structuredClone(node)
        delete updateNode.dragging
        return updateNode
      })

      updatedAllNodes = applyIntersectionStyles(updatedAllNodes)

      setNodes(updatedAllNodes)

      setNodesHighlighted({ groupId: "", nodesId: [] })
    },
    [
      applyIntersectionStyles,
      getInternalNode,
      getNodes,
      isLocked,
      reactFlowInstance,
      setNodes,
      setNodesHighlighted,
    ],
  )

  const handleDrag: OnNodeDrag<AppNode> = useThrottle(
    useCallback(
      (event, _node, nodes) => {
        // we are throttling onDrag callback so we need to make sure that it won't be invoked after drag stop
        if (!isDraggingNodesRef.current) return

        const flowPosition = reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        })
        const intersecting = getNodesIntersectingWithGroup(nodes, flowPosition, reactFlowInstance)

        const groupId = intersecting.group?.id ?? ""
        const nodesId = intersecting.nodes.map((n) => n.id)
        setNodesHighlighted({ groupId, nodesId })
      },
      [reactFlowInstance, setNodesHighlighted],
    ),
  )

  const handleKeybindingPasteNodes = useCallback(() => {
    if (!cursorPosition.current) return
    const flowPosition = screenToFlowPosition(cursorPosition.current)

    handlePasteNodes(flowPosition)
  }, [handlePasteNodes, screenToFlowPosition])

  const handleBeforeNodesEdgesDelete: OnBeforeDelete<AppNode, Edge> = useCallback(
    async ({ nodes, edges }) => {
      // Edge-only deletion
      if (nodes.length === 0) {
        isDeletingNodesRef.current = false
        // Return filtered edges (only allow unlocked edges to be deleted)
        return {
          nodes: [],
          edges: edges.filter((edge) => {
            if (isLocked) return false
            const typedEdge = edge as Edge<EdgeData>
            const isEdgeCurrentlyLocked = !!getEdgeLock(props.graphId ?? "", typedEdge, getNode)
            return !isEdgeCurrentlyLocked
          }),
        }
      }

      const isTryingDeleteLockedNode = nodes.some((node) => lockedNodesIds.includes(node.id))

      if (isTryingDeleteLockedNode) {
        enqueueSnackbar("Cannot delete locked node(s)", { variant: "error" })
        return false
      }

      // Node deletion with confirmation
      if (nodesToConfirmDeleting.length > 0) {
        setNodesToConfirmDeleting([])
        // Set flag before React Flow processes the deletion
        isDeletingNodesRef.current = true
        return true
      }

      setNodesToConfirmDeleting(nodes)
      return false
    },
    [getNode, isLocked, nodesToConfirmDeleting, props.graphId, lockedNodesIds],
  )

  const handleNodesDelete = (deletedNodes: AppNode[]) => {
    try {
      for (const { id } of deletedNodes) {
        removeNodeValidation(id)
      }

      setNodes((currentNodes) => {
        const allNodesUpdatedMap = new Map(currentNodes.map((n) => [n.id, n]))

        for (const deletedNode of deletedNodes) {
          const childrenNodes = getChildrenNodes(deletedNode, currentNodes)

          for (const childNode of childrenNodes) {
            const freshChild = allNodesUpdatedMap.get(childNode.id)
            if (!freshChild) continue

            const updatedNodes = removeEdgeFromNode(
              deletedNode,
              freshChild,
              Array.from(allNodesUpdatedMap.values()),
            )

            updatedNodes.forEach((node) => allNodesUpdatedMap.set(node.id, node))
          }
        }

        return Array.from(allNodesUpdatedMap.values())
      })
    } finally {
      // Reset flag after node deletion is complete (always runs even if error occurs)
      isDeletingNodesRef.current = false
    }
  }

  const handleConfirmDeletingNodes = useCallback(
    (isConfirmed: boolean) => {
      if (isConfirmed) reactFlowInstance.deleteElements({ nodes: nodesToConfirmDeleting })

      setNodesToConfirmDeleting([])
    },
    [nodesToConfirmDeleting, reactFlowInstance],
  )

  const handleOpenEdgesDialog = useCallback(() => {
    if (isLocked) return

    const sourceNodeIds = []

    for (const node of getNodes()) {
      if (node.selected && node.type !== NodeType.Group) {
        sourceNodeIds.push(node.id)
      }
    }

    setCreateEdgesDialogOpen({
      open: true,
      sourceNodeIds: sourceNodeIds,
      initialDirection: "Parents",
    })
    setPaneMenu(null)
  }, [isLocked, getNodes, setCreateEdgesDialogOpen, setPaneMenu])

  const handleLockGraph: SetState<boolean> = (newValue) => {
    if (remotelyLockedBy) return

    setIsLocked(newValue)
  }

  return (
    <>
      <ReactFlow
        key={props.graphId} // Force remount on graph change to reset all internal React Flow state
        // CORE
        edgeTypes={edgeTypes}
        edges={edges}
        // @ts-expect-error -- nodeTypes type mismatch with what ReactFlow do expect
        nodeTypes={nodeTypes}
        nodes={nodes}
        ref={ref}
        proOptions={{ hideAttribution: true }}
        // HANDLERS
        onBeforeDelete={handleBeforeNodesEdgesDelete}
        onNodesDelete={handleNodesDelete}
        onConnect={handleConnect}
        onConnectEnd={handleConnectEnd}
        onEdgeContextMenu={handleEdgeContextMenuOpen}
        onEdgesChange={onEdgesChanges}
        onEdgesDelete={handleEdgesDelete}
        onInit={handleInit}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMove={handlePanMove}
        onNodeClick={handleNodeClick}
        onNodeContextMenu={handleNodeContextMenuOpen}
        onNodeDoubleClick={handleNodeDoubleClick}
        onNodeDrag={handleDrag}
        onNodeDragStart={handleNodeDragStart}
        onNodeDragStop={handleNodeDragStop}
        onNodesChange={handleNodesChange}
        onPaneClick={handlePaneClick}
        onPaneContextMenu={handlePaneContextMenuOpen}
        onViewportChange={handleViewportChange}
        // CONFIG
        className={cls(
          "builder-graph-editor",
          canSelectInsideGroup && "no-pointer",
          isLocked && "builder-graph-editor--locked",
        )}
        connectionLineType={ConnectionLineType.Straight}
        deleteKeyCode={isLocked ? [] : ["Delete", "Backspace"]}
        elementsSelectable={addNodeMode === AddNodeMode.None} // prevent selecting nodes in case of trying to add node directly to existing group
        elevateEdgesOnSelect={true}
        elevateNodesOnSelect={false} // has to be disabled, otherwise it'll keep changing zIndex (groups in groups)
        fitView={true}
        isValidConnection={isValidConnection}
        maxZoom={3}
        minZoom={0.05}
        nodesConnectable={!isLocked}
        nodesDraggable={!isLocked}
        onlyRenderVisibleElements={true}
        panOnDrag={!isLocked ? [1] : [0, 1]} // 0 - left click, 1 - scroll click
        panOnScroll={false}
        selectNodesOnDrag={false}
        selectionMode={canSelectInsideGroup ? SelectionMode.Full : SelectionMode.Partial}
        selectionOnDrag={!isLocked}
        snapToGrid={snapToGrid.active}
        snapGrid={[snapToGrid.size, snapToGrid.size]}
      >
        {isRendering && (
          <Grid container alignContent="center" justifyContent="center" sx={{ height: "100%" }}>
            <Grid item>
              <CircularProgress disableShrink sx={{ position: "relative", zIndex: 4 }} />
            </Grid>
          </Grid>
        )}
        {isLocked && <GraphLockedInfo onGraphUnlockClick={() => setIsLocked(false)} />}
        {!isLocked && <KeyBinding bind={shortcuts["copy-nodes"]} onKeyDown={handleCopyNodes} />}
        {!isLocked && (
          <KeyBinding bind={shortcuts["paste-nodes"]} onKeyDown={handleKeybindingPasteNodes} />
        )}

        {!isLocked && (
          <KeyBinding bind={shortcuts["create-edges"]} onKeyUp={handleOpenEdgesDialog} />
        )}

        <Background gap={snapToGrid.size} variant={BackgroundVariant.Dots} />

        <Controls position="top-left" showZoom={false} showFitView={false} showInteractive={false}>
          <ZoomInButton />
          <ZoomOutButton />
          <FitViewButton />
          <GraphInteractionToggleButton onLockGraph={handleLockGraph} isOn={!isLocked} />
          <SelectInsideGroupToggleButton
            onCanSelectChange={setCanSelectInsideGroup}
            isOn={canSelectInsideGroup}
          />
          <ActiveShortcutsInfoButton />
        </Controls>

        {nodeMenu && <ContextNodeMenu {...nodeMenu} onOpenEdgesDialog={handleOpenEdgesDialog} />}
        {paneMenu && <ContextPaneMenu {...paneMenu} onOpenEdgesDialog={handleOpenEdgesDialog} />}
        {edgeMenu && <ContextEdgeMenu {...edgeMenu} onOpenEdgesDialog={handleOpenEdgesDialog} />}

        {nodesToDuplicate.nodes.length !== 0 && <RenameDialog />}

        <ErrorsDrawer />

        {props.graphId && <CursorOverlay graphId={props.graphId} />}
        <ReactFlowDevTools />
      </ReactFlow>
      <NodeDrawer graphId={props.graphId} disabled={isLocked} />
      <Box
        className="ghost-node-for-edit"
        sx={{ display: addNodeMode === AddNodeMode.None ? "none" : "block" }}
        ref={ghostNodeRef}
      />
      {nodesToConfirmDeleting.length > 0 && (
        <ConfirmDeletingNodesDialog
          nodes={nodesToConfirmDeleting}
          onClose={handleConfirmDeletingNodes}
        />
      )}
      {props.graphId && !isLocked && (
        <AddNodeButton sx={{ position: "absolute", bottom: 20, right: 20 }} />
      )}
    </>
  )
}
